import { StatusCode } from "api/protocols";
import { GetCreditMemo } from "api/rpc/2024-04/facilityAdmin/order/creditMemo";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import DropFilter from "components/dropdown/DropFilter";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import Page from "components/page/Page";
import Search from "components/search/Search";
import { capitalize, errorMessage } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { showError } from "redux/actions/ui";
import { ICreditMemo } from "redux/reducers/models/creditMemo";

const TABLE_LIMIT = 50;
const CREDIT_MEMO_STATUS_FILTER_KEY = "credit_memo_status_filters";

type CreditMemoFilters = {
    status: Array<string>;
};

export default function CreditMemos() {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { filterHistoryStore } = useAppSelector(store => store);
    const [creditMemos, setCreditMemos] = useState<Array<ICreditMemo>>([]);
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [filters, setFilters] = useState<CreditMemoFilters>({
        status: filterHistoryStore[CREDIT_MEMO_STATUS_FILTER_KEY]
          ? filterHistoryStore[CREDIT_MEMO_STATUS_FILTER_KEY].currentFilter
          : [],
      });

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getCreditMemos(source.token);
        return () => source.cancel();
    }, [offset, searchQuery, filters]);

    async function getCreditMemos(cancelToken?: CancelToken) {
        if (creditMemos !== undefined) {
            setCreditMemos(undefined);
        }
        const res = await GetCreditMemo({ offset: offset, limit: TABLE_LIMIT, search: searchQuery, statuses: filters?.status }, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            setCreditMemos([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(res, t("Error getting credit memos"))));
            return;
        }
        setCreditMemos(res?.data);
    }

    function handleExtendedFilter(filterReturn: Record<string, any>[], type: keyof CreditMemoFilters) {
        setFilters(prev => ({
          ...prev,
          [type]: filterReturn.map(filter => filter.id as string),
        }));
      }

    return (
        <Page
            title={t("Credit Memos")}
            narrow
            primaryAction={{ content: t("New"), action: () => history.push("/admin/settings/credit-memos/new") }}
        >
            <DropFilter
                title={t("Status")}
                filterData={[{id: "draft", label: t("Draft")}, {id: "finalized", label: t("Finalized")}]}
                filterLabelPropFromData="label"
                filterIdPropFromData="id"
                filterType="Checkbox"
                historyKey={CREDIT_MEMO_STATUS_FILTER_KEY}
                applyFilters={filterReturn => handleExtendedFilter(filterReturn, "status")}
                defaultCheckboxes={filters?.status}
            />
            <br />
            <br />
            <Search
                historyKey={"credit-memos-search"}
                searchCallback={search => setSearchQuery(search)}
                placeholder={t("Search...")}
                showDropdown={false}
            />
            <br />
            <DataTable
                columns={[{ label: t("Credit Memo Number") }, { label: t("Vendor") }, { label: t("Status") }]}
                loading={creditMemos === undefined}
                footer={{
                    tableLimit: TABLE_LIMIT,
                    tableOffset: offset,
                    disableNextOffset: !creditMemos || !(creditMemos?.length === TABLE_LIMIT),
                    handleTableOffset: direction => direction === "prev" ? setOffset(prev => prev - TABLE_LIMIT) : setOffset(prev => prev + TABLE_LIMIT),
                }}
            >
                {searchQuery?.length > 0 && creditMemos?.length === 0 ?
                    <tr>
                        <td colSpan={3}><MessageDisplay icon="magnifying-glass" title={t("No credit memos found")} content={t("Adjust your search and try again")} />
                        </td>
                    </tr> : creditMemos?.length === 0 ?
                        <tr>
                            <td colSpan={3}><MessageDisplay icon="magnifying-glass" title={t("No credit memos created")} content={t("Create a new credit memo to populate this list")} />
                            </td>
                        </tr> : creditMemos?.map((creditMemo, index) => {
                            return (
                                <tr key={index} className="clickable" onClick={() => history.push(`/admin/settings/credit-memos/${creditMemo?.id}`)}>
                                    <td>{creditMemo?.credit_memo_number}</td>
                                    <td>{creditMemo?.vendor_name}</td>
                                    <td><Badge size="small" type={creditMemo?.status === "draft" ? "gray" : "success"}>{capitalize(creditMemo?.status)}</Badge></td>
                                </tr>
                            );
                        })}
            </DataTable>

        </Page>
    );
}